import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from '@reach/router';
import { connect } from 'react-redux';
import { selectDataWeatherAlert } from 'state/selectors/notificationCenterSelectors';
import { Loading, NotFound, GlobalContext, I18NContext, I18NConsumer } from 'commons';
import { setPageTitle, orgId } from 'utils';
import * as notificationCenterActions from 'state/actions/notificationCenterActions';
import NoResult from 'containers/notification-center/NoResult';
import Header from './Header';
import Banner from './Banner';
import TextMessage from './TextMessage';
import PolygonMap from './PolygonMap';
import CircleMap from './CircleMap';
import EffectedAreas from './EffectedAreas';

const WeatherAlertDetail = ({
  weatherAlertId: id,
  getWeatherAlert,
  onClearCache,
  weatherAlert: {
    responseCode = 200,
    title,
    originalFeed: {
      feature: { geometry, properties = {} } = {},
      time: originalFeedTime, polygons, points,
    } = {},
  },
}) => {
  const i18n = useContext(I18NContext);
  const { organization: { orgName } } = useContext(GlobalContext);

  useEffect(() => {
    if (id) {
      getWeatherAlert({ id });
    }

    setTimeout(() => window.scrollTo(0, 0));

    return onClearCache;
  }, []);

  switch (responseCode) {
    case 200:
      break;
    case 404:
      return <Redirect noThrow to={`${orgId}/weather`} />;
    default:
      return <NotFound orgName={orgName} />;
  }

  const coordinates = (type => {
    if (type === 'Polygon') {
      return geometry.coordinates;
    }

    if (type === 'MultiPolygon') {
      return geometry.coordinates.map(coordinate => Array.prototype.concat.apply([], coordinate));
    }

    if (!type && polygons) {
      return polygons
        .map(polygon => polygon.points)
        .map(item => item.map(coordinate => [coordinate.x, coordinate.y]));
    }

    return null;
  })(geometry && geometry.type);

  if (!id) {
    return <Loading />;
  }

  const { issuanceTime, endTime, textBody } = properties;
  const regFilter = /^[\s\S]*INCLUDING THE CIT\w* OF ((.*,\s*\r{2}\n)*.*(?:[^,]\r{2}\n))[\s\S]*$/;
  const matchResults = textBody && textBody.match(regFilter);
  const effectedAreas = matchResults && matchResults[1].replace(/\n\s?/g, '');

  return (
    <div className="weather-alert">
      <I18NConsumer>
        {() => setPageTitle(`${orgName} - ${i18n['weather.title']} - ${title}`)}
      </I18NConsumer>
      <Header {...{ expired: endTime && new Date(`${endTime}Z`) < new Date(), title }} />
      <Banner {...{ issuanceTime, originalFeedTime, endTime }} />
      {!textBody && (
        <NoResult
          className="col-sm-8 not-empty"
          message="weather.notification.noneProvided"
        />
      )}
      <div className="row">
        <div className="col-sm-8">
          {
            textBody &&
              <div tabIndex="0">
                <article eb-test="ee_weatherAlertDetail_text_body">
                  <TextMessage text={textBody} />
                </article>
              </div>
          }
        </div>
        <div className="col-sm-4">
          {coordinates && coordinates.length > 0 && (
            <div eb-test="ee_weatherAlertDetail_field_map">
              <PolygonMap coordinates={coordinates} />
            </div>
          )}
          {!coordinates && points && (
            <div eb-test="ee_weatherAlertDetail_field_map">
              <CircleMap points={points} />
            </div>
          )}
          {effectedAreas && <EffectedAreas text={effectedAreas} />}
        </div>
      </div>
    </div>
  );
};

WeatherAlertDetail.propTypes = {
  weatherAlertId: PropTypes.string.isRequired,
  weatherAlert: PropTypes.shape().isRequired,
  getWeatherAlert: PropTypes.func.isRequired,
  onClearCache: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  weatherAlert: selectDataWeatherAlert(state),
});

const mapDispatchToProps = dispatch => ({
  getWeatherAlert: ({ id }) => dispatch(notificationCenterActions.getWeatherAlert.action({ id })),
  onClearCache: () => dispatch(notificationCenterActions.onClearCache({
    weatherAlert: { responseCode: 200 },
  })),
});

export default connect(mapStateToProps, mapDispatchToProps)(WeatherAlertDetail);
